<template>
  <div>
    <div class="ui-grid row">
      <div v-if="vm && vm.suspension.status" class="col-md-12">
        <vuestic-widget :headerText="$t('management.suspensionTitle')">
          <vuestic-alert type="warning" :withCloseBtn="false">
            <span class="badge badge-pill badge-warning">{{
              "management.suspensionReason" | translate
            }}</span>
            {{
              vm.suspension.reason.length > 0
                ? vm.suspension.reason
                : $t("management.overduePayment")
            }}
          </vuestic-alert>
        </vuestic-widget>
      </div>
    </div>

    <div class="row">
      <div v-if="reinstall || restore || migrating" class="col-md-12">
        <template v-if="reinstall">
          <vuestic-widget :headerText="$t('management.reinstallTitle')">
            <div class="well">{{ $t("management.reinstallBody") }}</div>
          </vuestic-widget>
        </template>
        <template v-if="restore">
          <vuestic-widget :headerText="$t('management.restoreTitle')">
            <div class="well">{{ $t("management.restoreBody") }}</div>
          </vuestic-widget>
        </template>
        <template v-if="migrating">
          <vuestic-widget :headerText="$t('management.migratingTitle')">
            <div class="well">{{ $t("management.migratingBody") }}</div>
          </vuestic-widget>
        </template>
      </div>
    </div>

    <vuestic-modal
      :show.sync="modalInfo.show"
      v-bind:small="true"
      v-bind:force="true"
      ref="modalInfo"
      :cancelClass="'none'"
      :okText="'modal.close' | translate"
    >
      <div slot="title">{{ modalInfo.title }}</div>
      <div>{{ modalInfo.body }}</div>
    </vuestic-modal>

    <div class="row" v-if="!reinstall && !restore && !migrating">
      <div class="col-md-12">
        <vuestic-widget :headerText="$t('management.headerServerConfig')">
          <div class="row">
            <div class="col-xs-12 col-md-12">
              <div class="table-responsive" style="overflow-x: visible">
                <table class="table table-striped first-td-padding">
                  <tbody>
                    <tr>
                      <td>
                        <b>Status</b>
                      </td>
                      <td>
                        <span
                          v-if="vm && vm.status.status === 'running'"
                          class="badge badge-pill badge-success"
                          >{{ "management.online" | translate }}</span
                        >
                        <span v-else class="badge badge-pill badge-danger">{{
                          "management.offline" | translate
                        }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>Uptime</b>
                      </td>
                      <td>{{ vm ? formatUptime(vm.status.uptime) : null }}</td>
                    </tr>
                    <tr>
                      <td>
                        <b>{{ $t("management.cpuUsage") }}</b>
                      </td>
                      <td>
                        {{
                          vm
                            ? (vm.status.cpu * 100).toFixed(2) +
                              "% (" +
                              (vm.status.cpus + "x vCPU)")
                            : null
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>{{ $t("management.memoryUsage") }}</b>
                      </td>
                      <td>
                        {{
                          vm
                            ? formatMemory(vm.status.mem, vm.status.maxmem)
                            : null
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>{{ $t("management.diskSize") }}</b>
                      </td>
                      <td>{{ vm ? formatDisk(vm.status.maxdisk) : null }}</td>
                    </tr>
                    <tr v-if="vm && vm.network ? vm.network.limit > 0 : false">
                      <td>
                        <b>{{ $t("management.networkUsage") }}</b>
                      </td>
                      <td>
                        <div
                          v-if="vm && vm.network"
                          :class="{
                            'network-meter': vm.network,
                            'network-meter-alert': vm.network.percentage > 80,
                            'network-meter-danger': vm.network.percentage > 100,
                          }"
                        >
                          <span
                            :style="{
                              width:
                                (vm.network.percentage > 100
                                  ? 100
                                  : vm.network.percentage) + '%',
                            }"
                          ></span>
                        </div>
                        {{ vm ? formatNetworkUsage(vm.network) : null }}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <b>Hostname</b>
                      </td>
                      <td>
                        <div
                          class="form-group form-group-w-btn"
                          style="margin: 0"
                        >
                          <div class="input-group" style="max-width: 100px">
                            <input
                              id="input-w-btn-round"
                              v-model="hostname"
                              required
                            />
                            <label
                              class="control-label"
                              for="input-w-btn-round"
                            >
                              {{ "management.hostname" | translate }}
                            </label>
                            <i class="bar"></i>
                          </div>
                          <div
                            id="hostname-base-domain"
                            class="input-group"
                            style="max-width: 150px"
                          >
                            <vuestic-simple-select
                              :label="'management.baseDomain' | translate"
                              v-model="selectedDomain"
                              option-key="domain"
                              v-bind:options="hostnameDomains"
                            ></vuestic-simple-select>
                          </div>
                          <div
                            class="btn btn-primary btn-with-icon btn-micro rounded-icon"
                            style="margin-left: 10px"
                            @click="changeHostname()"
                          >
                            <div class="btn-with-icon-content">
                              <i
                                class="ion-ios-checkmark-circle-outline ion"
                              ></i>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <CustomIsoTableSettings
                      :key="Math.random()"
                      :vm="vm ? vm : {}"
                      v-on:reloadVmSettings="pageSetup()"
                    />
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </vuestic-widget>
      </div>
    </div>

    <!--
    <div v-if="!reinstall && !restore && !migrating" class="row">
      <CustomIsoEditInVps :key="Math.random()" :serverId="(vm) ? vm.data.server_id : null"
                          :vmId="(vm) ? vm.data.id : null" v-on:reloadVmSettings="pageSetup()"/>
    </div>
    -->

    <div
      class="row"
      v-if="!reinstall && !restore && !migrating && showFeatures"
    >
      <div class="col-md-12">
        <vuestic-widget :headerText="$t('management.headerControlPanel')">
          <div class="row">
            <div class="col-md-4 col-sm-8">
              <button
                class="btn-custom btn btn-primary btn-with-icon"
                @click="showMediumModal('start')"
              >
                <div class="btn-with-icon-content">
                  <i class="ion-md-power ion"></i>
                  {{ $t("management.start") }}
                </div>
              </button>
            </div>

            <div class="col-md-4 col-sm-8">
              <button
                class="btn-custom btn btn-danger btn-with-icon"
                @click="showMediumModal('stop')"
              >
                <div class="btn-with-icon-content">
                  <i class="ion-md-close ion"></i>
                  {{ $t("management.shutdown") }}
                </div>
              </button>
            </div>

            <div class="col-md-4 col-sm-8">
              <button
                class="btn-custom btn btn-warning btn-with-icon"
                @click="showMediumModal('restart')"
              >
                <div class="btn-with-icon-content">
                  <i class="ion-md-repeat ion"></i>
                  {{ $t("management.restart") }}
                </div>
              </button>
            </div>
          </div>

          <div class="row" style="margin-top: 60px">
            <div class="col-md-4 col-sm-8">
              <a target="popup" :onclick="noVncWindow()">
                <button class="btn-custom btn btn-secondary btn-with-icon">
                  <div class="btn-with-icon-content">
                    <i class="ion-md-eye ion"></i>
                    {{ $t("management.access_server") }}
                  </div>
                </button>
              </a>
            </div>

            <div class="col-md-4 col-sm-8">
              <button
                class="btn-custom btn btn-info btn-with-icon"
                @click="showReinstallModal()"
              >
                <div class="btn-with-icon-content">
                  <i class="ion-md-trash ion"></i>
                  {{ $t("management.reinstall") }}
                </div>
              </button>
            </div>

            <div class="col-md-4 col-sm-8">
              <button
                class="btn-custom btn btn-dark btn-with-icon"
                @click="scheduleBackupModal()"
              >
                <div class="btn-with-icon-content">
                  <i class="ion-md-time ion"></i>
                  {{ $t("management.scheduleBackup") }}
                </div>
              </button>
            </div>
          </div>
        </vuestic-widget>
      </div>
    </div>

    <div
      class="row eq-widget-height"
      v-if="!reinstall && !restore && !migrating"
    >
      <div class="col-md-6">
        <vuestic-widget :headerText="$t('management.headerBackupList')">
          <div class="row">
            <div class="col-xs-12 col-md-12">
              <div class="table-responsive">
                <table class="table table-striped first-td-padding">
                  <thead>
                    <tr>
                      <td>{{ $t("management.date") }}</td>
                      <td>{{ $t("management.file") }}</td>
                      <td>{{ $t("management.restore") }}</td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, key) in vm ? vm.data.backups : null"
                      :id="key"
                      :key="key"
                    >
                      <td>{{ time(item.created_at, "datetime") }}</td>
                      <td>{{ item.volid }}</td>
                      <td>
                        <button
                          class="btn btn-primary btn-micro btn-with-icon rounded-icon"
                          @click="showRestoreModal(item)"
                        >
                          <div class="btn-with-icon-content">
                            <i class="ion-md-refresh ion"></i>
                          </div>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </vuestic-widget>
      </div>

      <div class="col-md-6">
        <vuestic-widget :headerText="$t('management.headerIpList')">
          <div class="row">
            <div class="col-xs-12 col-md-12">
              <div class="table-responsive">
                <table class="table table-striped first-td-padding">
                  <thead>
                    <tr>
                      <td>IP</td>
                      <td>MAC</td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, key) in vm ? vm.data.vm_nics : null"
                      :id="key"
                      :key="key"
                    >
                      <td>{{ item.ip.ip_address }}</td>
                      <td>{{ item.ip.mac_address }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </vuestic-widget>
      </div>
    </div>

    <vm-usage
      ref="vmUsageComponent"
      :vm="this.vm.data"
      v-if="!reinstall && !restore && !migrating && this.vm"
    ></vm-usage>

    <vuestic-modal
      :show.sync="modal.show"
      ref="mediumModal"
      :okText="'modal.confirm' | translate"
      v-on:ok="confirm"
      :cancelText="'modal.cancel' | translate"
    >
      <div slot="title">{{ modal.title }}</div>
      <div>{{ modal.body }}</div>
    </vuestic-modal>

    <vuestic-modal
      :show.sync="reinstallModal.show"
      v-bind:large="true"
      ref="reinstallModal"
      :okText="'modal.confirm' | translate"
      v-on:ok="confirmReinstall"
      :okDisabled="!reinstallModal.selectedTemplate || passworDisabled"
      :cancelText="'modal.cancel' | translate"
    >
      <div slot="title">
        {{ "management.reinstallModal.title" | translate }}
      </div>

      <div class="reinstall">
        <multiselect
          v-model="reinstallModal.selectedTemplate"
          placeholder=""
          label="display_name"
          track-by="display_name"
          :options="templatesList"
          :option-height="42"
          :custom-label="customLabel"
          group-values="templates"
          group-label="type"
          :group-select="true"
          :autocomplete="true"
          :show-labels="false"
        >
          <template slot="singleLabel" slot-scope="props">
            <img
              class="option__image"
              :src="BASE_URL + '/img/so/' + props.option.img"
              :alt="props.option.real_name"
            />
            <span class="option__title">{{ props.option.display_name }} </span>
          </template>
          <template slot="option" slot-scope="props">
            <span v-if="props.option.display_name">
              <img
                class="option__image"
                :src="BASE_URL + '/img/so/' + props.option.img"
                :alt="props.option.real_name"
              />
              <span class="option__title">{{ props.option.display_name }}</span>
            </span>
            <span v-else>
              <span class="option__title">{{ props.option.$groupLabel }}</span>
            </span>
          </template>
        </multiselect>

        <select
          class="custom-select my-select"
          v-model="reinstallModal.selectedCategory"
          v-if="windowWidth > 620"
        >
          <option
            v-for="(type, index) in reinstallModal.categories"
            :key="index"
            :value="type"
          >
            {{ type }}
          </option>
        </select>
      </div>
      <div v-if="windowWidth <= 620">
        <select
          class="custom-select my-select"
          v-model="reinstallModal.selectedCategory"
        >
          <option
            v-for="(type, index) in reinstallModal.categories"
            :key="index"
            :value="type"
          >
            {{ type }}
          </option>
        </select>
      </div>

      <div
        v-if="
          reinstallModal.selectedTemplate &&
          reinstallModal.selectedTemplate.choose_password
        "
        class="template-password"
        ref="templatePassword"
      >
        <h3>
          <strong>{{ $t("passwordStrength.passwordServer") }}</strong>
        </h3>

        <VuePasswordStrength
          :badge="false"
          :secureLength="8"
          v-on:password="setTemplatePassword($event)"
        />
      </div>
    </vuestic-modal>

    <vuestic-modal
      :show.sync="backupModal.show"
      ref="backupModal"
      :okText="'modal.save' | translate"
      v-on:ok="saveBackupConfig"
      :cancelText="'modal.cancel' | translate"
      :okDisabled="
        backupModal.config.sun ||
        backupModal.config.mon ||
        backupModal.config.tue ||
        backupModal.config.wed ||
        backupModal.config.thu ||
        backupModal.config.fri ||
        backupModal.config.sat
          ? false
          : false
      "
    >
      <div slot="title">
        {{ "management.backupModal.backupTitle" | translate }}
      </div>
      <div class="well">
        <p>
          <b>
            {{ "management.backupModal.backupBody1" | translate }}
            {{ backupModal.backup_slots }}
            {{ "management.backupModal.backupBody2" | translate }}
          </b>
          {{ "management.backupModal.backupBody3" | translate }}
        </p>
      </div>
      <hr style="margin-top: 5px; margin-bottom: 5px" />
      <div class="row">
        <div class="col-md-5">
          <h4>{{ "management.backupModal.dayOfWeek" | translate }}</h4>
          <fieldset>
            <vuestic-checkbox
              :label="'management.backupModal.sun' | translate"
              id="checkbox1"
              v-model="backupModal.config.sun"
            />
            <vuestic-checkbox
              :label="'management.backupModal.mon' | translate"
              id="checkbox2"
              v-model="backupModal.config.mon"
            />
            <vuestic-checkbox
              :label="'management.backupModal.tue' | translate"
              id="checkbox3"
              v-model="backupModal.config.tue"
            />
            <vuestic-checkbox
              :label="'management.backupModal.wed' | translate"
              id="checkbox4"
              v-model="backupModal.config.wed"
            />
            <vuestic-checkbox
              :label="'management.backupModal.thu' | translate"
              id="checkbox5"
              v-model="backupModal.config.thu"
            />
            <vuestic-checkbox
              :label="'management.backupModal.fri' | translate"
              id="checkbox6"
              v-model="backupModal.config.fri"
            />
            <vuestic-checkbox
              :label="'management.backupModal.sat' | translate"
              id="checkbox7"
              v-model="backupModal.config.sat"
            />
          </fieldset>
        </div>
        <div class="col-md-6">
          <h4>{{ "management.backupModal.hour" | translate }}</h4>
          <fieldset>
            <div class="form-group">
              <div class="input-group">
                <vuestic-date-picker
                  id="date-picker-basic"
                  v-model="backupModal.config.time"
                  :config="{
                    enableTime: true,
                    noCalendar: true,
                    dateFormat: 'H:i',
                    time_24hr: true,
                    defaultDate: '00:00',
                  }"
                />
                <label class="control-label" for="date-picker-basic">{{
                  $t("management.backupModal.time")
                }}</label>
                <i class="bar"></i>
              </div>
            </div>
          </fieldset>
          <div class="well">{{ $t("management.backupModal.well") }}</div>
        </div>
      </div>
    </vuestic-modal>

    <vuestic-modal
      :show.sync="restoreModal.show"
      ref="restoreModal"
      :okText="'modal.confirm' | translate"
      v-on:ok="restoreVm"
      :cancelText="'modal.cancel' | translate"
    >
      <div slot="title">{{ "management.restoreModal.title" | translate }}</div>
      <div class="well">
        <p style="color: red">
          {{ "management.restoreModal.body1" | translate }}
          <b>{{
            restoreModal.selected
              ? time(restoreModal.selected.created_at, "datetime")
              : ""
          }}</b>
          ?
        </p>
        <p>{{ "management.restoreModal.body2" | translate }}</p>
      </div>
    </vuestic-modal>
  </div>
</template>

<script>
import Vue from "vue";
import store from "../../store";
import ajax from "services/ajax";
import configs from "services/configs";
import time from "services/time";
import moment from "moment";
import { getAxios } from "services/ajax";

import VmUsage from "./VmUsage";
import CustomIsoEditInVps from "../CustomIso/CustomIsoEditInVps";
import CustomIsoTableSettings from "../CustomIso/CustomIsoTableSettings";
//import Select2 from 'v-select2-component';
import VuePasswordStrength from "../forms/form-elements/VuePasswordStrength";

Vue.component("vm-usage", VmUsage);
//Vue.component('Select2', Select2);

export default {
  name: "management",
  data() {
    return {
      hostnameDomains: [],
      selectedDomain: {},
      hostname: "",
      reinstall: false,
      migrating: false,
      restore: false,
      showFeatures: false,
      showOsSettings: true,
      vm: null,
      modal: {
        show: true,
        title: "",
        body: "",
        selected: "",
      },
      reinstallModal: {
        show: true,
        templates: [],
        selectedIndex: null,
        selectedTemplate: null,
        categories: [],
        selectedCategory: this.$t("management.reinstallModalAllCategories"),
      },
      backupModal: {
        show: true,
        backup_slots: 0,
        config: {
          time: "00:00",
          sun: true,
          mon: false,
          tue: false,
          wed: false,
          thu: false,
          fri: false,
          sat: false,
        },
      },
      modalInfo: {
        show: true,
        title: "",
        body: "",
      },
      restoreModal: {
        show: true,
        selected: {},
      },
      BASE_URL: configs.BASE_URL,
      windowWidth: window.innerWidth,
      password: null,
      passworDisabled: false
    };
  },
  components: {
    CustomIsoEditInVps,
    CustomIsoTableSettings,
    VuePasswordStrength,
  },
  created() {
    this.pageSetup();
  },
  watch: {
    hostname(newVal) {
      newVal = newVal.toLowerCase();
      let re = /[^A-Z0-9]/gi;
      this.$set(this, "hostname", newVal.replace(re, ""));
    },
    reinstallModal: {
      handler: function (val) {
        if (val.selectedTemplate && val.selectedTemplate.choose_password && !this.password) {
          this.passworDisabled = true;
        } else {
          this.passworDisabled = false
        }

      },
      deep: true,
    },
    password: {
      handler: function (val) {
        if (this.reinstallModal.selectedTemplate && this.reinstallModal.selectedTemplate.choose_password && !this.password) {
          this.passworDisabled = true;
        } else {
          this.passworDisabled = false
        }

      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener("resize", this.onResize);
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  computed: {
    templatesList() {
      if (
        this.reinstallModal.selectedCategory !=
        this.$t("management.reinstallModalAllCategories")
      ) {
        return this.reinstallModal.templates.filter((template) => {
          return template.type === this.reinstallModal.selectedCategory;
        });
      } else {
        return this.reinstallModal.templates;
      }
    },
  },
  methods: {
    setTemplatePassword(val) {
      this.password = val;
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    pageSetup() {
      var hostingId = this.$route.params.id;
      store.commit("setLoading", true);
      ajax.run(
        this,
        "customer/vms/" + hostingId + "/is-cloning",
        (data) => {
          if (data.cloning) {
            this.reinstall = true;
            store.commit("setLoading", false);
          } else {
            ajax.run(
              this,
              "customer/vms/" + hostingId,
              (data) => {
                store.commit("setLoading", false);
                this.vm = data;
                this.vm.network = data.network;

                if (this.vm.suspension.status) {
                  this.showFeatures = false;
                } else {
                  this.showFeatures = true;
                }
                this.defaultDomain();
              },
              (error) => {
                store.commit("setLoading", false);
                console.log(error);
              }
            );
          }
        },
        (error) => {
          store.commit("setLoading", false);
          console.log(error);
        }
      );
      ajax.run(
        this,
        "customer/hostnames",
        (data) => {
          for (var i = 0; i < data.hostnames.length; i++) {
            data.hostnames[i].domain = "." + data.hostnames[i].domain;
          }
          this.hostnameDomains = data.hostnames;
          this.defaultDomain();
        },
        (error) => {
          console.log(error);
        }
      );
      ajax.run(
        this,
        "customer/vms/" + hostingId + "/is-migrating",
        (data) => {
          if (data.migrating) {
            this.migrating = true;
          }
        },
        (error) => {
          console.log(error);
        }
      );
    },
    customLabel({ display_name, desc }) {
      return `${display_name} – ${desc}`;
    },
    myChangeEvent(val) {
      console.log(val);
    },
    mySelectEvent({ id, text }) {
      console.log({ id, text });
    },
    changeHostname: function () {
      if (this.hostname.length < 4) {
        this.modalInfo.title = this.$t("management.hostnameError.title");
        this.modalInfo.body = this.$t("management.hostnameError.bodyMinLength");
        this.$refs.modalInfo.open();
        return;
      }
      store.commit("setLoading", true);
      ajax.run(
        this,
        "customer/hostnames",
        (data) => {
          setTimeout(function () {
            store.commit("setLoading", false);
          }, 2000);
          if (data.success) {
            this.showToast(this.$t("management.hostnameChanged"), {
              icon: "fa-star-o",
              position: "top-center",
              duration: 3000,
              fullWidth: false,
            });
          } else {
            switch (data.metadata.error_code) {
              case 10:
                this.modalInfo.title = this.$t(
                  "management.hostnameError.title"
                );
                this.modalInfo.body = this.$t(
                  "management.hostnameError.bodyInUse"
                );
                this.$refs.modalInfo.open();
                break;
              default:
                this.modalInfo.title = this.$t(
                  "management.hostnameError.title"
                );
                this.modalInfo.body = this.$t("management.hostnameError.body");
                this.$refs.modalInfo.open();
            }
          }
        },
        (error) => {
          store.commit("setLoading", false);
          this.modalInfo.title = this.$t("management.hostnameError.title");
          this.modalInfo.body = this.$t("management.hostnameError.body");
          this.$refs.modalInfo.open();
          console.log(error);
        },
        "post",
        {
          hosting_id: this.$route.params.id,
          hostname_domain_id: this.selectedDomain.id,
          hostname: this.hostname,
        }
      );
    },
    defaultDomain: function () {
      if (this.vm && this.vm.data.hostname && this.hostnameDomains.length) {
        this.hostname = this.vm.data.hostname.hostname;
        for (var i = 0; i < this.hostnameDomains.length; i++) {
          if (
            this.hostnameDomains[i].id ===
            this.vm.data.hostname.hostname_domain_id
          ) {
            this.selectedDomain = this.hostnameDomains[i];
          }
        }
      } else {
        this.selectedDomain = this.hostnameDomains[0];
      }
    },
    time: function (date, format) {
      return time.convert(date, format);
    },
    formatUptime: function (uptime) {
      if (uptime < 60) {
        return uptime + " " + this.$t("management.seconds");
      }
      if (uptime < 3600) {
        return parseInt(uptime / 60) + " " + this.$t("management.minutes");
      }
      if (uptime < 86400) {
        return parseInt(uptime / 60 / 60) + " " + this.$t("management.hours");
      }
      return parseInt(uptime / 60 / 60 / 24) + " " + this.$t("management.days");
    },
    formatMemory: function (used, total) {
      return (
        parseInt(used / 1024 / 1024) +
        " MB " +
        this.$t("management.used") +
        " / " +
        parseInt(total / 1024 / 1024) +
        " MB " +
        this.$t("management.total")
      );
    },
    formatDisk: function (total) {
      return parseInt(total / 1024 / 1024 / 1024) + " GB";
    },
    formatNetworkUsage(network) {
      const mbUsed = Math.round(network.net);
      const limit = network.limit ? Math.round(network.limit) : 0;
      const percentage = network.percentage.toFixed(2);

      const thisMonth = this.getMonthDateRange(
        moment().format("YYYY"),
        moment().format("M")
      );

      const startMonth =
        Vue.i18n.locale() !== "br"
          ? new Date(thisMonth.start).toLocaleDateString()
          : thisMonth.start;
      const endMonth =
        Vue.i18n.locale() !== "br"
          ? new Date(thisMonth.end).toLocaleDateString()
          : thisMonth.end;

      return `${percentage}% | ${mbUsed} MB ${this.$t(
        "management.used"
      )} / ${limit} TB ${this.$t("management.total")} (${this.$t(
        "upgrade.from"
      )} ${startMonth} ${this.$t("upgrade.until")} ${endMonth})`;
    },
    showMediumModal(action) {
      switch (action) {
        case "start":
          this.modal.selected = "start";
          this.modal.title = this.$t("management.modal.startTitle");
          this.modal.body = this.$t("management.modal.startBody");
          break;
        case "stop":
          this.modal.selected = "stop";
          this.modal.title = this.$t("management.modal.stopTitle");
          this.modal.body = this.$t("management.modal.stopBody");
          break;
        case "restart":
          this.modal.selected = "restart";
          this.modal.title = this.$t("management.modal.restartTitle");
          this.modal.body = this.$t("management.modal.restartBody");
          break;
        default:
          return;
      }
      this.$refs.mediumModal.open();
    },
    confirm() {
      store.commit("setLoading", true);
      var selected = this.modal.selected;
      var hostingId = this.$route.params.id;
      switch (selected) {
        case "start":
          ajax.run(
            this,
            "customer/vms/unsuspend",
            (data) => {
              setTimeout(function () {
                store.commit("setLoading", false);
              }, 2000);
              this.showToast(this.$t("management.toast"), {
                icon: "fa-star-o",
                position: "top-center",
                duration: 3000,
                fullWidth: false,
              });
            },
            (error) => {
              store.commit("setLoading", false);
              console.log(error);
            },
            "put",
            { hosting_id: hostingId }
          );
          break;
        case "stop":
          ajax.run(
            this,
            "customer/vms/suspend",
            (data) => {
              setTimeout(function () {
                store.commit("setLoading", false);
              }, 2000);
              this.showToast(this.$t("management.toast"), {
                icon: "fa-star-o",
                position: "top-center",
                duration: 3000,
                fullWidth: false,
              });
            },
            (error) => {
              store.commit("setLoading", false);
              console.log(error);
            },
            "put",
            { hosting_id: hostingId }
          );
          break;
        case "restart":
          ajax.run(
            this,
            "customer/vms/reset",
            (data) => {
              setTimeout(function () {
                store.commit("setLoading", false);
              }, 2000);
              this.showToast(this.$t("management.toast"), {
                icon: "fa-star-o",
                position: "top-center",
                duration: 3000,
                fullWidth: false,
              });
            },
            (error) => {
              store.commit("setLoading", false);
              console.log(error);
            },
            "put",
            { hosting_id: hostingId }
          );
          break;
      }
    },
    showReinstallModal() {
      this.reinstallModal.selectedIndex = null;
      this.reinstallModal.selectedTemplate = null

      var hostingId = this.$route.params.id;

      this.password = null
      this.passworDisabled = false

      store.commit("setLoading", true);
      ajax.run(
        this,
        "customer/vms/" + hostingId + "/available-templates",
        (data) => {
          store.commit("setLoading", false);
          this.reinstallModal.templates = data.templates.map((template) => {
            return template;
          });
          this.reinstallModal.categories = [
            this.$t("management.reinstallModalAllCategories"),
            ...data.categories,
          ];
          this.$refs.reinstallModal.open();
        },
        (error) => {
          store.commit("setLoading", false);
          console.log(error);
        }
      );
    },
    confirmReinstall() {
      if (this.reinstallModal.selectedTemplate) {
        var template = this.reinstallModal.selectedTemplate;
        var hostingId = this.$route.params.id;

        store.commit("setLoading", true);
        ajax.run(
          this,
          "customer/vms/reinstall",
          (data) => {
            store.commit("setLoading", false);
            this.reinstall = true;
          },
          (error) => {
            store.commit("setLoading", false);
            console.log(error);
          },
          "put",
          {
            hosting_id: hostingId,
            template_id: template.id,
            software_password: this.password,
          }
        );
      }
    },
    scheduleBackupModal() {
      var hostingId = this.$route.params.id;
      store.commit("setLoading", true);
      ajax.run(
        this,
        "customer/vms/" + hostingId + "/backup-cron",
        (data) => {
          store.commit("setLoading", false);
          if (data.cron) {
            this.backupModal.config = data.cron;
          }
          this.backupModal.backup_slots = data.backup_slots;
          this.$refs.backupModal.open();
        },
        (error) => {
          store.commit("setLoading", false);
          console.log(error);
        }
      );
    },
    saveBackupConfig() {
      var hostingId = this.$route.params.id;
      store.commit("setLoading", true);
      ajax.run(
        this,
        "customer/vms/" + hostingId + "/save-backup-cron",
        (data) => {
          setTimeout(function () {
            store.commit("setLoading", false);
          }, 2000);
          this.showToast(this.$t("management.backupModal.success"), {
            icon: "fa-star-o",
            position: "top-center",
            duration: 3000,
            fullWidth: false,
          });
        },
        (error) => {
          store.commit("setLoading", false);
          if (error.response && error.response.status === 412) {
            this.modalInfo.title = this.$t(
              "management.backupModal.backupTitle"
            );
            this.modalInfo.body = this.$t("management.backupModal.noSlots");
            this.$refs.modalInfo.open();
          }
          console.log(error);
        },
        "post",
        this.backupModal.config
      );
    },
    showRestoreModal(item) {
      this.restoreModal.selected = item;
      this.$refs.restoreModal.open();
    },
    restoreVm() {
      var hostingId = this.$route.params.id;
      store.commit("setLoading", true);
      ajax.run(
        this,
        "customer/vms/" +
          hostingId +
          "/restore-backup/" +
          this.restoreModal.selected.id,
        (data) => {
          setTimeout(function () {
            store.commit("setLoading", false);
          }, 2000);
          this.showToast(this.$t("management.restoreModal.success"), {
            icon: "fa-star-o",
            position: "top-center",
            duration: 3000,
            fullWidth: false,
          });
          this.restore = true;
        },
        (error) => {
          store.commit("setLoading", false);
          if (error.response && error.response.status === 412) {
            this.modalInfo.title = this.$t(
              "management.backupModal.backupTitle"
            );
            this.modalInfo.body = this.$t("management.backupModal.noSlots");
            this.$refs.modalInfo.open();
          }
          console.log(error);
        },
        "post",
        []
      );
    },
    noVncWindow() {
      var hostingId = this.$route.params.id;
      var routeData = this.$router.resolve("/novnc/" + hostingId + `?token=${localStorage.getItem("token")}`);
      return 'window.open("' + routeData.href + '", "","width=900,height=700")';
    },
    getMonthDateRange(year, month) {
      // month in moment is 0 based, so 9 is actually october, subtract 1 to compensate
      // array is 'year', 'month', 'day', etc
      var startDate = moment([year, month - 1]);

      // Clone the value before .endOf()
      var endDate = moment(startDate).endOf("month");

      // make sure to call toDate() for plain JavaScript date type
      return {
        start: time.convert(startDate, "date"),
        end: time.convert(endDate, "date"),
      };
    },
  },
};
</script>

<style lang="scss">
  @import "../../sass/_variables.scss";
  @import "~bootstrap/scss/mixins/breakpoints";
  @import "~bootstrap/scss/functions";
  @import "~bootstrap/scss/variables";

.btn-custom {
  width: 100%;
}

#hostname-base-domain .form-group {
  margin: 0;
}

.position-box {
  margin-right: 2px;
  cursor: pointer;
  background-color: $brand-primary;
  opacity: 0.6;

  &:last-child {
    margin-right: 0;
  }

  &:hover {
    opacity: 0.9;
  }

  &.selected {
    opacity: 1;
    border-style: solid;
  }
}

@media only screen and (min-width: 768px) {
  .eq-widget-height .widget {
    height: 87%;
  }
}

/** Network Progress Bar */
.network-meter {
  width: 40%;
  margin-right: 5px;
  height: 20px;
  position: relative;
  background: #555;
  border-radius: 5px;
  padding: 5px;
  box-shadow: inset 0 -1px 1px rgba(255, 255, 255, 0.3);
}

.network-meter > span {
  display: block;
  height: 100%;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
  background-color: rgb(43, 194, 83);
  background-image: -webkit-gradient(
    linear,
    left bottom,
    left top,
    color-stop(0, rgb(43, 194, 83)),
    color-stop(1, rgb(84, 240, 84))
  );
  box-shadow: inset 0 2px 9px rgba(255, 255, 255, 0.3),
    inset 0 -2px 6px rgba(0, 0, 0, 0.4);
  position: relative;
  overflow: hidden;
}

.network-meter-alert > span {
  background-image: -webkit-gradient(
    linear,
    left bottom,
    left top,
    color-stop(0, #7ead22),
    color-stop(1, #fbf300)
  );
}

.network-meter-danger > span {
  background-image: -webkit-gradient(
    linear,
    left bottom,
    left top,
    color-stop(0, #c22b32),
    color-stop(1, #fb0000)
  );
}

.network-meter > span:after,
.animate > span > span {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-image: -webkit-gradient(
    linear,
    0 0,
    100% 100%,
    color-stop(0.25, rgba(255, 255, 255, 0.2)),
    color-stop(0.25, transparent),
    color-stop(0.5, transparent),
    color-stop(0.5, rgba(255, 255, 255, 0.2)),
    color-stop(0.75, rgba(255, 255, 255, 0.2)),
    color-stop(0.75, transparent),
    to(transparent)
  );
  z-index: 1;
  -webkit-background-size: 50px 50px;
  -webkit-animation: move 2s linear infinite;
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  overflow: hidden;
}

@-webkit-keyframes move {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 50px 50px;
  }
}

.nostripes > span > span,
.nostripes > span:after {
  -webkit-animation: none;
  background-image: none;
}

/** Network Progress Bar END */

.option__image {
  width: 20px;
  height: 20px;
  margin-right: 5px;
}

.my-select {
  background-color: #ededed;
  color: #5f6162;
  border: 0 none;
  border-radius: 1px 20px 20px 1px;
  height: 42px;
  width: 200px;
  padding: 6px 20px;
}

.reinstall {
  display: flex;
}

.template-password {
  margin-top: 1rem;
}

@include media-breakpoint-down(md) {
  .btn-custom {
    margin-bottom: 15px;
  }
}

</style>

<!-- multi-select -->
<style src="vue-multiselect/dist/vue-multiselect.min.css">
</style>



// WEBPACK FOOTER //
// src/components/management/Management.vue